import React, { useEffect, useState } from 'react';

import { CTA, InputAppearanceValue } from '@vw-marketing/us-components';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { StyledPinFormWrapper } from '../styles/pin-form-styles';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import {
  onVWBasicFormCtaClickEvent,
  onVWBasicFormErrorMessageLoadEvent,
  onVWBasicFormStepLoadEvent,
} from '../../../shared/tagging/tagging-helpers';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import { NoTranslateTextInput } from '../../../shared/components/no-translate-text-input';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';

type HandleTextInputEvent = (
  e: React.SyntheticEvent<HTMLInputElement, Event>,
) => void;

interface PinFormProps {
  onComplete: () => void;
}

export const PinForm = ({ onComplete }: PinFormProps) => {
  const [appearance, setAppearance] = useState<InputAppearanceValue>('default');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [trackedErrorCodes, setTrackedErrorCodes] = useState<
    Record<string, boolean>
  >({});

  const { authService, translationHandler } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();

  const isFrench = localeService?.language === 'fr';
  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const errorMessages = {
    empty: translationHandler('registration.pin.error.empty'),
    digitsOnly: translationHandler('registration.pin.error.digits'),
    length: translationHandler('registration.pin.error.length'),
  };

  const validationSchema = Yup.object({
    pin: Yup.string()
      .matches(/^[0-9]{4}$/, 'PIN must be exactly 4 digits')
      .required('PIN is required'),
  });

  const trackingManager = useTrackingManager();

  const handleFormCTAClickTracking = () => {
    onVWBasicFormCtaClickEvent(
      trackingManager,
      {
        Type: 'Button',
        Name: translationHandler('registration.pin.confirm.cta.label'),
      },
    );
  };

  const handleStepLoadTracking = () => {
    onVWBasicFormStepLoadEvent(trackingManager, {});
  };

  const handleErrorMessageLoadTracking = ({
    code,
    message,
    referringUrl,
  }: {
    code: string;
    message: string;
    referringUrl: string;
  }) => {
    if (!trackedErrorCodes[code]) {
      onVWBasicFormErrorMessageLoadEvent(
        trackingManager,
        {
          code,
          message,
          referringUrl,
          source: 'Form',
        },
      );
      setTrackedErrorCodes((prev) => ({
        ...prev,
        [code]: true,
      }));
    }
  };

  const formik = useFormik({
    initialValues: { pin: '' },
    onSubmit: async (values) => {
      setIsSubmitting(true);

      const pinObject = {
        pin: {
          value: values.pin,
        },
      };

      let response: any;

      // * This is for local development
      // response = await authService.mockSaveSecurityPin(pinObject);

      // * This is for integration and prod
      response = await authService.saveSecurityPin(pinObject, localeCode);

      handleFormCTAClickTracking();

      if (
        response?.createSecurityPin?.creationStatus === 'SECURITY_PIN_CREATED'
      ) {
        // Navigate to step 3
        onComplete();
      } else {
        setAppearance('error');
        setErrorMessage('Failed to create PIN. Please try again.');

        handleErrorMessageLoadTracking({
          code: 'PIN_CREATION_FAILED',
          message: 'Failed to create PIN. Please try again.',
          referringUrl: window.location.href,
        });
      }
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema,
  });

  const validatePin = (value: string) => {
    if (value.length === 4 && /^\d*$/.test(value)) {
      setAppearance('default');
      setErrorMessage('');

      setTrackedErrorCodes({});
      return true;
    }

    if (!value && isTouched) {
      setAppearance('error');
      setErrorMessage(errorMessages.empty);
      handleErrorMessageLoadTracking({
        code: 'PIN_EMPTY',
        message: errorMessages.empty,
        referringUrl: window.location.href,
      });
      return false;
    } else if (!/^\d*$/.test(value)) {
      setAppearance('error');
      setErrorMessage(errorMessages.digitsOnly);
      handleErrorMessageLoadTracking({
        code: 'PIN_INVALID_DIGITS',
        message: errorMessages.digitsOnly,
        referringUrl: window.location.href,
      });
      return false;
    } else if (value.length > 0 && value.length !== 4) {
      setAppearance('error');
      setErrorMessage(errorMessages.length);
      handleErrorMessageLoadTracking({
        code: 'PIN_LENGTH_INVALID',
        message: errorMessages.length,
        referringUrl: window.location.href,
      });
      return false;
    } else {
      setAppearance('default');
      setErrorMessage('');
      return true;
    }
  };

  const handleBlur: HandleTextInputEvent = (e) => {
    const value = e.currentTarget.value;

    formik.setFieldTouched('pin', true);
    setIsTouched(true);
    validatePin(value);
  };

  const handleChange: HandleTextInputEvent = (e) => {
    const value = e.currentTarget.value;

    if (value === '' || /^\d*$/.test(value)) {
      formik.setFieldValue('pin', value);

      if (isTouched) validatePin(value);
    }
  };

  useEffect(() => {
    handleStepLoadTracking();
  }, []);

  useEffect(() => {
    if (isTouched) {
      validatePin(formik.values.pin);
    }
  }, [isTouched, formik.values.pin]);

  const isButtonDisabled =
    formik.values.pin.length !== 4 ||
    !formik.isValid ||
    formik.isSubmitting ||
    appearance === 'error' ||
    isSubmitting;

  return (
    <form onSubmit={formik.handleSubmit}>
      <StyledPinFormWrapper>
        <NoTranslateTextInput
          selectors={['p']}
          appearance={appearance}
          aria-describedby="pin-error"
          aria-label="4 digit password field"
          aria-required="true"
          characterCountLimit={0}
          inputMode="numeric"
          label={isFrench ? 'NIP' : 'PIN'}
          maxLength={4}
          message={errorMessage}
          name="pin"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          togglePasswordVisibilityIconLabel="toggle visibility"
          type="password"
          value={formik.values.pin}
        />
        <CTA
          aria-label="Submit button"
          emphasis="primary"
          stretchContent
          tag="button"
          type="submit"
          disabled={isButtonDisabled}
        >
          {translationHandler('registration.pin.confirm.cta.label')}
        </CTA>
      </StyledPinFormWrapper>
    </form>
  );
};
